/**
 * Endpoints para gestión de sucursales usando localStorage
 * Modo demo sin backend
 */
import { storage, STORAGE_KEYS, delay } from "../config";

// Generar ID único
const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

/**
 * Listar todas las sucursales
 */
export const list = async (page = 1) => {
  await delay(200);
  const branches = storage.get(STORAGE_KEYS.BRANCHES, []);
  
  // Simular paginación
  const pageSize = 10;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;
  const results = branches.slice(start, end);
  
  return {
    results,
    count: branches.length,
    page,
    page_size: pageSize,
    next: end < branches.length ? page + 1 : null,
    previous: page > 1 ? page - 1 : null,
  };
};

/**
 * Obtener una sucursal por ID
 */
export const retrieve = async (id) => {
  await delay(200);
  const branches = storage.get(STORAGE_KEYS.BRANCHES, []);
  const branch = branches.find((b) => b.id === id);
  
  if (!branch) {
    throw new Error("Sucursal no encontrada");
  }
  
  return branch;
};

/**
 * Crear una nueva sucursal
 */
export const create = async (data) => {
  await delay(300);
  const branches = storage.get(STORAGE_KEYS.BRANCHES, []);
  
  const newBranch = {
    id: generateId(),
    ...data,
    created: new Date().toISOString(),
    modified: new Date().toISOString(),
  };
  
  branches.push(newBranch);
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.BRANCHES, branches);
    
    // Verificar que la sucursal se guardó correctamente
    const savedBranches = storage.get(STORAGE_KEYS.BRANCHES, []);
    const savedBranch = savedBranches.find((b) => b.id === newBranch.id);
    
    if (!savedBranch) {
      throw new Error("La sucursal no se guardó correctamente en localStorage");
    }
    
    return newBranch;
  } catch (error) {
    console.error("Error al guardar sucursal:", error);
    throw error;
  }
};

/**
 * Actualizar una sucursal
 */
export const update = async (id, data) => {
  await delay(300);
  const branches = storage.get(STORAGE_KEYS.BRANCHES, []);
  const index = branches.findIndex((b) => b.id === id);
  
  if (index === -1) {
    throw new Error("Sucursal no encontrada");
  }
  
  branches[index] = {
    ...branches[index],
    ...data,
    modified: new Date().toISOString(),
  };
  
  // Guardar y verificar que se guardó correctamente
  try {
    storage.set(STORAGE_KEYS.BRANCHES, branches);
    
    // Verificar que la sucursal se actualizó correctamente
    const savedBranches = storage.get(STORAGE_KEYS.BRANCHES, []);
    const savedBranch = savedBranches.find((b) => b.id === id);
    
    if (!savedBranch) {
      throw new Error("La sucursal no se actualizó correctamente en localStorage");
    }
    
    return branches[index];
  } catch (error) {
    console.error("Error al actualizar sucursal:", error);
    throw error;
  }
};

/**
 * Eliminar una sucursal
 */
export const destroy = async (id) => {
  await delay(200);
  const branches = storage.get(STORAGE_KEYS.BRANCHES, []);
  const filtered = branches.filter((b) => b.id !== id);
  
  // Guardar y verificar que se eliminó correctamente
  try {
    storage.set(STORAGE_KEYS.BRANCHES, filtered);
    
    // Verificar que la sucursal se eliminó correctamente
    const savedBranches = storage.get(STORAGE_KEYS.BRANCHES, []);
    const deletedBranch = savedBranches.find((b) => b.id === id);
    
    if (deletedBranch) {
      throw new Error("La sucursal no se eliminó correctamente de localStorage");
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error al eliminar sucursal:", error);
    throw error;
  }
};

/**
 * Obtener sucursales en formato optimizado para select
 */
export const getBranchesSelect = async () => {
  await delay(200);
  const branches = storage.get(STORAGE_KEYS.BRANCHES, []);
  
  return branches.map((branch) => ({
    value: branch.id,
    label: branch.business_name || branch.commercial_business,
  }));
};
